import { AnimatePresence, motion, useAnimation } from "framer-motion";
import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import {
    analysis,
    arrowL,
    arrowR,
    boost,
    callOutgoing,
    challenge,
    dragDrop,
    group,
    groupOpen,
    homeContent1,
    homeContent2,
    homeContent3,
    homeContent4,
    homeMain,
    job,
    machineLearning,
    optimizeResources,
    phone,
    strengthenCollaboration,
} from "../assets/images/image";
import { APP_URL } from "../configs/default.config";
import HomeMobile from "../layouts/mobile/HomeMobile";

const defaultReset = 7000;

const Home = () => {
    const [positionContent, setPositionContent] = React.useState(1);
    const [direction, setDirection] = React.useState<"left" | "right">("left");
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: false
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3,
            }
        }
    };

    const fadeInVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    };

    const slideInFromLeft = {
        hidden: { x: -100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const slideInFromRight = {
        hidden: { x: 100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const content = [
        {
            position: 1,
            url: homeContent1,
            className: "w-full flex-shrink-0",
            text: "Auto-generate crystal-clear task briefs",
        },
        {
            position: 2,
            url: homeContent2,
            className: "h-[350px] flex-shrink-0",
            text: "AI-prioritized schedules at a glance",
        },
        {
            position: 3,
            url: homeContent3,
            className: "h-[350px] flex-shrink-0",
            text: "Smart load-balancing across skillsets",
        },
        {
            position: 4,
            url: homeContent4,
            className: "h-[350px] flex-shrink-0",
            text: "AI-prioritized schedules at a glance",
        },
    ];

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setPositionContent((prev) => (prev < 4 ? prev + 1 : 1));
        }, defaultReset);

        return () => clearTimeout(timeout);
    }, [positionContent]);

    const handleChangeContent = (type: "next" | "prev") => {
        setPositionContent((prev) => {
            if (type === "next") return prev < 4 ? prev + 1 : 1;
            return prev > 1 ? prev - 1 : 4;
        });
    };

    const workContent = [
        {
            position: 1,
            url: machineLearning,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[28px] mb-[34px] text-base font-bold leading-none w-full text-center">Smart Task Briefs & Idea Labs</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Instant task descriptions with <strong>built-in context, deadlines and resource estimates</strong>.</li>
                        <li>Brainstorm mode kicks off project proposals in <strong>seconds</strong>.</li>
                    </ul>
            `
        },
        {
            position: 2,
            url: dragDrop,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[28px] mb-[34px] text-base font-bold leading-none w-full text-center">Drag-and-Drop with Live AI</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Move cards and <strong>watch AI flag potential bottlenecks</strong> with color-coded alerts.</li>
                        <li>In-board tips: <em>"Consider assigning Maria—she's 20% under capacity"</em></li>
                    </ul>
            `
        },
        {
            position: 3,
            url: challenge,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[28px] mb-[34px] text-base font-bold leading-none w-full text-center">AI-Backed Assignment Engine</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Profiles auto-updated with <strong>skills, past performance, availability</strong>.</li>
                        <li>One-click team suggestions with time-to-complete predictions.</li>
                    </ul>
            `
        },
        {
            position: 4,
            url: analysis,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[28px] mb-[34px] text-base font-bold leading-none w-full text-center">Dynamic Analytics & Forecasting</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li><strong>Interactive charts</strong> that update as tasks move.</li>
                        <li>"What if" slider for <strong>workload spikes and deadline shifts</strong>.</li>
                    </ul>
            `
        },
        {
            position: 5,
            url: job,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[28px] mb-[34px] text-base font-bold leading-none w-full text-center">Industry-Tailored Templates</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Select from Marketing, Software, Healthcare or Custom Matrix.</li>
                        <li>Pre-built integrations: GitHub, Jira, Salesforce, Asana.</li>
                    </ul>
            `
        },
    ];

    const challengeContent = [
        {
            position: 1,
            url: boost,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[20px] text-base font-semibold leading-none text-center">Boost Productivity</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Automated briefs</strong> and <strong>smart assignments</strong> free your team to invent, not busywork.</li>
            </ul>
          `
        },
        {
            position: 2,
            url: strengthenCollaboration,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[20px] text-base font-semibold leading-none text-center">Strengthen Collaboration</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Live updates</strong>, shared AI notes and in-app comments <strong>eliminate miscommunication</strong>.</li>
            </ul>
          `
        },
        {
            position: 3,
            url: group,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[20px] text-base font-semibold leading-none text-center">Deliver Consistently</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Predictive timelines</strong> and <strong>proactive alerts</strong> keep every milestone <strong>on track</strong>.</li>
            </ul>
          `
        },
        {
            position: 4,
            url: optimizeResources,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[20px] text-base font-semibold leading-none text-center">Optimize Resources:</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li>Cut costs with data-driven workload balancing and avoid last-minute scrambles.</li>
            </ul>
          `
        }
    ];

    return (
        <>
            <div className="block md:hidden mt-[100px]">
                <HomeMobile />
            </div>
            <div className="hidden md:block">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                    className="flex flex-col md:flex-row items-center justify-between mt-[73px]"
                >
                    <motion.div variants={itemVariants} className="w-[40%] space-y-6">
                        <p className="text-[24px] font-bold leading-[normal] text-[#B1B1B1]">
                            Unleash Project Potential – Empower Your Team
                        </p>
                        <h1 className="text-[68px] leading-[72px] text-[#494949] font-[274]">
                            Deliver projects{" "}
                            <span className="df-color text-[68px] font-black not-italic leading-[72px]">
                                30% Faster
                            </span>{" "}
                            <br />
                            with intelligent task automation
                        </h1>

                        <p className="text-[26px] leading-[40px] font-normal text-[#494949] my-[16px]">
                            Automate assignments, track performance, and hit deadlines with intelligent
                            insights—no manual juggling required.
                        </p>

                        <div className="flex gap-4">
                            <button
                                onClick={() => window.location.href = APP_URL}
                                className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA]">
                                <span className="flex items-center justify-center mb-1">Try It for Free &nbsp;</span>
                                <img
                                    src={groupOpen}
                                    alt="Task automation mockup"
                                    className="h-[13.87px] w-[11.39px]"
                                />
                            </button>
                            <button onClick={() => window.location.href = "/contact"} className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] border border-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#494949]">
                                <span className="flex items-center justify-center mb-1">Contact Sales &nbsp;</span>
                                <img
                                    src={callOutgoing}
                                    alt="Task automation mockup"
                                    className="h-[24px] w-[24px]"
                                />
                            </button>
                        </div>
                    </motion.div>
                    <motion.img
                        variants={slideInFromRight}
                        src={homeMain}
                        alt="Task automation mockup"
                        className="w-[60%] flex-shrink-0"
                    />
                </motion.div>

                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        UB Dashboard adapts to your unique needs, serving a wide array of sectors including
                    </motion.div>
                    <motion.div variants={itemVariants} className="mt-[32px] flex flex-co space-x-[128px]">
                        <div className="text-[#494949] text-[16px] font-normal leading-[24px] w-[468px]">
                            Marketing, App Development, Light Industry, Oil & Gas, Industrial, Consumer
                            Goods, Healthcare, Consumer Services, Telecommunications, Utilities, Financial
                            Services, Technology, Materials, and more.
                        </div>
                        <div className="text-[#494949] text-[16px] font-normal leading-[24px] w-[468px]">
                            Ultimate Boards significantly reduce human resource and energy costs, boosting
                            team productivity and accelerating project completion through advanced AI.
                        </div>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariants}
                >
                    <div className="flex items-center gap-2 mt-[67px]">
                        <button
                            className="flex w-[56px] p-4 flex-col items-start gap-4 rounded-l-[16px] bg-[#3A57FF] me-[16px]"
                            onClick={() => handleChangeContent("prev")}
                        >
                            <img
                                src={arrowL}
                                alt="Previous"
                                className="w-[24px] h-[24px] flex-shrink-0"
                            />
                        </button>
                        <button
                            className="flex w-[56px] p-4 flex-col items-start gap-4 rounded-r-[16px] bg-[#3A57FF] me-[20px]"
                            onClick={() => handleChangeContent("next")}
                        >
                            <img
                                src={arrowR}
                                alt="Next"
                                className="w-[24px] h-[24px] flex-shrink-0"
                            />
                        </button>
                        <div className="inline-flex h-[56px] px-4 py-1 justify-center items-center gap-4 flex-shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-center font-[SF_Pro] text-[26px] font-bold leading-[40px]">
                            {content.find((dt) => dt.position === positionContent)?.text}
                        </div>
                    </div>

                    <div className="h-[450px] mt-[40px] flex items-center overflow-hidden relative w-full">
                        <AnimatePresence initial={false} mode="wait">
                            <motion.img
                                key={positionContent}
                                src={content[positionContent - 1].url}
                                alt={`content-${positionContent}`}
                                className={`${content[positionContent - 1].className} absolute mx-auto -translate-x-1/2`}
                                initial={{ x: direction === "left" ? 300 : -300, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: direction === "left" ? -300 : 300, opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                onDragEnd={(e, info) => {
                                    if (info.offset.x < -100) {
                                        setDirection("left");
                                        setPositionContent(prev => (prev < 4 ? prev + 1 : 1));
                                    } else if (info.offset.x > 100) {
                                        setDirection("right");
                                        setPositionContent(prev => (prev > 1 ? prev - 1 : 4));
                                    }
                                }}
                            />
                        </AnimatePresence>
                    </div>

                    <div className="flex items-center gap-4 mt-[64px]">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <button
                                key={index}
                                className={`h-1.5 w-6 rounded-full transition-all ${positionContent === index + 1 ? "bg-[#3A57FF]" : "bg-[#B0B0B0]"
                                    }`}
                                onClick={() => setPositionContent(index + 1)}
                            />
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        Meet the AI That Works for You
                    </motion.div>
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row mx-auto justify-between mt-[32px]"
                    >
                        {workContent.map((item) => (
                            <motion.div
                                key={item.position}
                                variants={itemVariants}
                                className="flex flex-col items-start gap-3 w-[250px]"
                            >
                                <div className="w-full flex items-center justify-center">
                                    <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: item.content }} />
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        From Challenges to Triumphs: The UB Dashboard Impact
                    </motion.div>
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row mx-auto justify-between mt-[32px]"
                    >
                        {challengeContent.map((item) => (
                            <motion.div
                                key={item.position}
                                variants={itemVariants}
                                className="flex flex-col items-start gap-3 w-[250px]"
                            >
                                <div className="w-full flex items-center justify-center">
                                    <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: item.content }} />
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-normal leading-normal mt-[86px]">
                        Measure Your Success: <span className="text-[#3A57FF] font-bold">UB Dashboard ROI</span>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                    className="flex flex-col md:flex-row justify-between mt-[73px]"
                >
                    <motion.div variants={slideInFromLeft} className="space-y-6 w-[50%] pe-5">
                        <h2 className="text-[#494949] text-[42px] md:text-[68px] font-normal leading-[72px]">
                            Embed a mini-app where visitors can plug in
                        </h2>

                        <div className="flex gap-4 w-full">
                            <div className="flex flex-col w-[300px]">
                                <p className="text-[#494949] text-[20px] md:text-[26px] font-[274] leading-[40px] mt-6">
                                    <span className="text-[#3A57FF] font-bold">Output: </span>
                                    projected time saved, cost reduction, and payoff period.
                                </p>
                                <button
                                    onClick={() => window.location.href = APP_URL}
                                    className="mt-[45px] inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA]">
                                    <span className="flex items-center justify-center mb-1">Get Started – It's Free &nbsp;</span>
                                    <img
                                        src={groupOpen}
                                        alt="Task automation mockup"
                                        className="h-[13.87px] w-[11.39px]"
                                    />
                                </button>
                            </div>

                            <div className="flex flex-col w-[475px] h-auto bg-[#3A57FF] mt-6 justify-center">
                                <div className="p-4 max-w-md mx-auto">
                                    <ul className="text-[#FAFAFA] text-[26px] font-bold leading-[40px]">
                                        <li className="flex items-center">
                                            <span >1. Team Size</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span >2. Average tasks per week</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span >3. Billable rate</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 space-x-4">
                            <h1 className="text-[#494949] text-center font-inter text-[64px] font-bold leading-none">
                                $20
                            </h1>
                            <div className="text-right mt-1">
                                <h2 className="ms-2 text-[#3A57FF] text-center text-[24px] font-bold leading-none">
                                    Pro Plan
                                </h2>
                                <p className="text-[#494949] text-[32px] font-normal leading-none font-inter">
                                    /Month
                                </p>
                            </div>
                        </div>
                    </motion.div>
                    <motion.img
                        variants={slideInFromRight}
                        src={phone}
                        alt="Task automation mockup"
                        className="w-[40%] flex-shrink-0"
                    />
                </motion.div>
            </div>
        </>
    );
};

export default Home;